import { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useToast } from "../context/ToastContext.js";
import { useVirtualHr } from "../lib/virtualHr.js";
import { useLookup } from "../context/LookupContext.js";
import { useConfirm } from "./ui/confirm-dialog.js";
import { Card } from "./ui/card.js";
import { Button } from "./ui/button.js";
import { Input } from "./ui/input.js";
import { AsyncButton } from "./ui/async-button.js";
import { SelectField } from "./ui/select-field.js";
import { DatePicker } from "./DatePicker.js";
import { UploadZone, uploadDocumentOnly } from "../lib/resumeUpload.js";
import type { DocSummary } from "./DocumentsOverviewStrip.js";

// Types already covered by their own dedicated upload box elsewhere in the
// profile -- excluded here so the generic uploader can't create a second,
// untracked document of a type that already has AI-extraction wiring.
// BGV_REPORT has its own HR-only block in the Onboarding section.
const FIXED_TYPES = new Set(["PAN", "AADHAAR", "OTHER", "FORM_11", "MARKSHEET", "DEGREE", "OFFER_LETTER", "BANK_DOCUMENT", "BGV_REPORT"]);

export function OtherDocumentsPanel({
  employeeId, isHR, isEditable, docs, onOpen, onChanged,
}: {
  employeeId: string;
  isHR: boolean;
  isEditable: boolean;
  docs: DocSummary[];
  onOpen: (doc: DocSummary) => void;
  onChanged: (type: string) => void;
}) {
  const toast = useToast();
  const vhr = useVirtualHr();
  const confirm = useConfirm();
  const docTypes = useLookup("DOCUMENT_TYPE").filter(o => !FIXED_TYPES.has(o.value));

  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // useLookup("DOCUMENT_TYPE") is empty on first render (it loads async) --
  // default to the first option once it arrives, without overriding a type
  // the user has already picked.
  useEffect(() => {
    if (!type && docTypes.length > 0) setType(docTypes[0]!.value);
  }, [docTypes, type]);

  const otherDocs = docs.filter(d => !FIXED_TYPES.has(d.type));

  async function upload() {
    if (!file || !type) throw new Error("validation");
    try {
      await uploadDocumentOnly(employeeId, file, type, { name: name || undefined, expiresAt: expiresAt || undefined, verify: vhr.isDocEnabled(type) });
      setName(""); setExpiresAt(""); setFile(null);
      onChanged(type);
    } catch (e) {
      toast.show(errMsg(e) ?? "Upload failed", "error");
      throw e;
    }
  }

  async function del(doc: DocSummary) {
    const ok = await confirm({
      title: `Delete "${doc.name}"?`,
      description: "This cannot be undone from this screen.",
      actionText: "Delete",
      tone: "destructive",
    });
    if (!ok) return;
    setDeletingId(doc.id);
    try {
      await api.delete(`/documents/${doc.id}`);
      onChanged(doc.type);
    } catch (err) {
      logError("OtherDocumentsPanel.delete", err);
      toast.show("Failed to delete document", "error");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <Card className="col-span-full p-4">
      <p className="mb-3 text-xs font-semibold text-surface-600">Other Documents</p>

      {isEditable && (
        // Explicit tracks: the date field needs ~150px for its text input plus the
        // calendar button, which an even 1fr column can squeeze past the card edge.
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(150px,0.8fr)]">
          <Input placeholder="Document name (optional)" value={name} onChange={e => setName(e.target.value)} />
          <SelectField value={type} onChange={e => setType(e.target.value)}>
            {docTypes.map(o => <option key={o.value} value={o.value}>{o.displayName}</option>)}
          </SelectField>
          <DatePicker value={expiresAt} onChange={setExpiresAt} placeholder="Expiry (optional)" />
          <div className="col-span-full flex min-w-0 flex-wrap items-center gap-2">
            <UploadZone label={file ? file.name : "Choose file"} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onFile={setFile} />
            <AsyncButton onClick={() => upload()} disabled={!file || !type} size="sm">Upload</AsyncButton>
          </div>
        </div>
      )}

      {otherDocs.length === 0 ? (
        <p className="text-xs text-surface-400">No other documents uploaded.</p>
      ) : (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-2">
          {otherDocs.map(doc => (
            <div key={doc.id} className="flex min-w-0 items-center justify-between gap-3 rounded-md border border-surface-100 bg-surface-50 px-3 py-2">
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-surface-800" title={doc.name}>{doc.name}</span>
              <div className="flex shrink-0 gap-2">
                <Button size="sm" variant="secondary" onClick={() => onOpen(doc)}>{isHR ? "Review" : "View"}</Button>
                {isHR && (
                  <Button size="sm" variant="destructive" disabled={deletingId === doc.id} onClick={() => del(doc)}>
                    <Trash2 size={12} />
                  </Button>
                )}
              </div>
            </div>
          ))}
          {otherDocs.some(d => d.status === "REJECTED" && d.rejectionReason) && (
            <div className="mt-1 text-[11px] text-error">
              {otherDocs.filter(d => d.status === "REJECTED" && d.rejectionReason).map(d => (
                <p key={d.id}>{d.name}: {d.rejectionReason}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
